/*
 * Copyright (c) 2026 CalAmp Corp.  All Rights Reserved
 */
import {useCallback, useEffect, useMemo} from 'react';
import {useQuery, useQueryClient} from 'react-query';
import {loadOfflineCanRequests} from '@ion/core/src/services/Connect';
import {
  LoadOfflineCanRequestsParams,
  OfflineCanApiError,
  OfflineCanRequestResult
} from '@ion/core/src/types/services';
import {createKey, QueryKey} from '../config/QueryKeys';
import {createQueryResult, DurationResult, getStaleTime} from '../utils/helpers';
import {UseDefaultQueryResult} from './types';

export enum OfflineCanApiErrorCode {
  NoCanRequestsFound = '404-CAN21'
}

/**
 * Normalizes offline CAN API errors from either a resolved response payload
 * or a rejected request payload.
 */
export const getOfflineCanApiErrors = (payload: any): OfflineCanApiError[] => {
  const errors = payload?.response?.errors || payload?.errors || [];
  return Array.isArray(errors) ? errors : [];
};

/**
 * Returns true when the API error payload indicates no CAN requests were found
 * for the selected filters.
 */
export const hasNoCanRequestsFoundError = (err: any): boolean => {
  const responseErrors = getOfflineCanApiErrors(err?.response?.data);
  return responseErrors.some((item) => `${item?.code || ''}` === OfflineCanApiErrorCode.NoCanRequestsFound);
};

export type UseLoadOfflineCanRequestsResult = UseDefaultQueryResult & {
  offlineCanRequests: OfflineCanRequestResult[];
  errors: OfflineCanApiError[];
  refresh: () => void;
};

type OfflineCanRequestsPayload = {
  results: OfflineCanRequestResult[];
  errors: OfflineCanApiError[];
};

type OfflineCanQueryError = {
  response?: {
    data?: any;
  };
};

export interface UseLoadOfflineCanRequestsOptions {
  params: string | LoadOfflineCanRequestsParams;
  staleTime?: number | DurationResult;
  enabled?: boolean;
  requireEsn?: boolean;
  queryKey?: QueryKey;
}

/**
 * Retrieves offline CAN requests for a specific ESN.
 *
 * @param esn       ESN for which offline CAN requests are to be retrieved.
 * @param staleTime how long the query should hold onto details before re-fetching.
 * @param queryKey  base query key, so a second lookup for the same ESN can be
 *                  cached apart from the CAN requests grid.
 */
export const useLoadOfflineCanRequests = ({
  params,
  staleTime = getStaleTime(1, false, false),
  enabled = false,
  requireEsn = true,
  queryKey = QueryKey.LOAD_OFFLINE_CAN_REQUESTS
}: UseLoadOfflineCanRequestsOptions): UseLoadOfflineCanRequestsResult => {
  const queryClient = useQueryClient();
  const queryParams = useMemo(() => {
    if (typeof params === 'string') {
      return {esn: params};
    }
    return params || {};
  }, [params]);
  const key = useMemo(() => (
    createKey(queryKey, queryParams)
  ), [queryKey, queryParams]);
  const isQueryEnabled = enabled && (!requireEsn || !!queryParams?.esn);
  const query = useQuery<OfflineCanRequestsPayload, OfflineCanQueryError>(key, async() => {
    const response = await loadOfflineCanRequests(queryParams);
    return response?.response || {results: [], errors: []};
  }, {
    staleTime : staleTime as number,
    enabled   : isQueryEnabled,
    retry     : (failureCount, err) => {
      if (hasNoCanRequestsFoundError(err)) {
        return false;
      }
      return failureCount < 2;
    }
  });

  useEffect(() => {
    if (!query.data && isQueryEnabled && !query.isError) {
      query.refetch();
    }
  }, [isQueryEnabled, query]);

  const refresh = useCallback(() => {
    queryClient.invalidateQueries(key);
  }, [key]);

  const apiErrors = useMemo(() => {
    if (query?.data) {
      return getOfflineCanApiErrors(query.data);
    }
    return getOfflineCanApiErrors(query?.error?.response?.data);
  }, [query?.data, query?.error]);

  return createQueryResult(query, {
    offlineCanRequests : query.data?.results || [],
    errors             : apiErrors,
    refresh
  }) as UseLoadOfflineCanRequestsResult;
};

export default useLoadOfflineCanRequests;
